import type { UIEvent } from "../../lib/events-adapter";
import { useEffect, useState } from "react";
import * as ApiClient from "@v0y4ge/api-client";
import { listHubs } from "@v0y4ge/api-client";
import type { Hub as HubDto } from "@v0y4ge/api-contracts";
import { studioRequestOptions } from "../../lib/studio-config";
import { logUpsert, logAction } from "../../lib/audit";
import { onTenantChange, onTenantSwitching } from "../../shell/access";

/**
 * Event Hubs — UI catalog + client-backed list hook.
 *
 * WIRED: `useHubs()` fetches via `listHubs()` and maps each contract `Hub` DTO →
 * the UI `Hub` at this boundary.
 *
 * REQ-014 RESOLVED (2026-07-25, Voyage pushback item 6): the full hubs family
 * ships in the typed client — `listHubs`/`getHub`/`createHub`/`updateHub`/
 * `deleteHub`/`duplicateHub`/`exportHubs`/`getPublicHub`. There is NO separate
 * publish verb by contract design (api-contracts/src/hubs.ts): publishing is
 * `updateHub({ status: 'published' })`, which sets `publishedAt`. The
 * `typeof op === "function"` guards below remain as kit-drift safety only;
 * mutations never fake success — a failed write surfaces the API's own error
 * to the caller (see HubEditor.tsx).
 */
export type HubKind = "location" | "group" | "workspace";

export interface HubFilterRule {
  kind?: HubKind;
  venue?: string; includeRule?: string; excludeRule?: string;
  tagRule?: string; autoPromote?: boolean; featuredEventId?: string;
  scope?: string; hideRule?: string;
}
export interface HubBranding { theme?: string; primaryColor?: string; logoUrl?: string; heroImageUrl?: string; }
export interface HubSeo { pageTitle?: string; metaDescription?: string; ogImageUrl?: string; }
export interface HubVisibility {
  indexed: boolean; showPastEvents: boolean; showCapacityRemaining: boolean; requireSignIn: boolean;
}

export interface Hub {
  id: string; kind: HubKind; name: string; slug: string;
  description?: string | null;
  events: number; visits: number; conv: number;
  status: "published" | "draft"; edited: string; custom: string;
  // REQ-014 fields — shipped; optional/defaulted for alias tolerance.
  filterRule?: HubFilterRule;
  branding?: HubBranding;
  seo?: HubSeo;
  visibility?: HubVisibility;
  publicUrl?: string | null;
  publicHost?: string | null;
  publishedAt?: string | null;
}

const DEFAULT_VISIBILITY: HubVisibility = { indexed: true, showPastEvents: true, showCapacityRemaining: false, requireSignIn: false };

/** Pick events for a hub by its filter rules (interim client-side derivation
 *  — becomes redundant once REQ-014's `filterRule` is server-evaluated). */
export function eventsForHub(hub: Hub, all: UIEvent[]): UIEvent[] {
  // Department hubs (2026-08-13): a tagRule names the department tag every
  // seed event carries (tags[0]) — membership derives from the events seed
  // rather than a duplicated id list, so reseeding events re-populates hubs.
  const tagRule = hub.filterRule?.tagRule;
  if (tagRule) return all.filter((e) => (e.tags || []).includes(tagRule));
  // The strict HubListResponse parse strips HubDetail-only fields (filterRules)
  // from list rows, so department hubs are authored with slug === tag and the
  // slug is the membership key that survives the wire. Claims the hub only when
  // the tag matches real events — legacy demo hubs fall through to the
  // heuristics below.
  if (hub.kind === "group") {
    const bySlug = all.filter((e) => (e.tags || []).includes(hub.slug));
    if (bySlug.length) return bySlug;
  }
  if (hub.kind === "workspace") return all;
  if (hub.kind === "location") {
    const key = hub.name.split(" · ")[0];
    return all.filter((e) => e.location.includes(key));
  }
  if (hub.slug === "memory-bank-2026") return all.filter((e) => /memory bank/i.test(e.title));
  if (hub.slug === "wealth-uk") return all.filter((e) => /wealth|mortgage|first-time/i.test(e.title));
  if (hub.slug === "tech-workshops") return all.filter((e) => /workshop|tech|onboarding/i.test(e.title));
  return all.slice(0, 4);
}

export const KIND_LABEL: Record<HubKind, string> = { location: "Location", group: "Series", workspace: "Workspace" };
export const KIND_TONE: Record<HubKind, "info" | "primary" | "success"> = { location: "info", group: "primary", workspace: "success" };

/** Contract Hub DTO → UI Hub (superset; alias-tolerant; REQ-014 fields optional). */
function hubFromDto(d: HubDto): Hub {
  const dd = d as HubDto & {
    eventsCount?: number; eventCount?: number; visits30d?: number; conversions?: number;
    filterRule?: HubFilterRule; branding?: HubBranding; seo?: HubSeo; visibility?: Partial<HubVisibility>;
    publicUrl?: string | null; publicHost?: string | null; publishedAt?: string | null;
    /** Contract HubDetail.filterRules — the canonical rule list (2026-08-13). */
    filterRules?: { kind: string; tag?: string; locationId?: string; seriesId?: string }[];
  };
  // Canonical contract rule → UI filterRule. byTag is the department-hub shape;
  // the legacy loose filterRule wins only when no canonical rules are present.
  const byTag = dd.filterRules?.find((r) => r.kind === "byTag");
  return {
    id: d.id,
    kind: d.kind as HubKind,
    name: d.name,
    slug: d.slug,
    events: d.events ?? dd.eventsCount ?? dd.eventCount ?? 0,
    visits: d.visits ?? dd.visits30d ?? 0,
    conv: d.conv ?? dd.conversions ?? 0,
    status: d.status === "draft" ? "draft" : "published",
    edited: d.edited || "—",
    custom: d.custom || "—",
    filterRule: byTag ? { kind: d.kind as HubKind, tagRule: byTag.tag } : (dd.filterRule ?? { kind: d.kind as HubKind }),
    branding: dd.branding ?? {},
    seo: dd.seo ?? {},
    visibility: { ...DEFAULT_VISIBILITY, ...(dd.visibility ?? {}) },
    publicUrl: dd.publicUrl ?? null,
    publicHost: dd.publicHost ?? null,
    publishedAt: dd.publishedAt ?? null,
  };
}

// Module store (parity with landing/templates) so a hub mutated from the list
// reflects immediately and the list can refetch after an op.
let _hubs: Hub[] | null = null;
let _hubsStarted = false;
const _hubSubs = new Set<() => void>();
const _hubEmit = () => _hubSubs.forEach((f) => { try { f(); } catch { /* noop */ } });
onTenantSwitching(() => { _hubs = null; _hubsStarted = false; _hubEmit(); });
onTenantChange(() => { void _loadHubs(true); });
async function _loadHubs(force = false): Promise<void> {
  if (_hubsStarted && !force) return;
  _hubsStarted = true;
  try {
    if (typeof listHubs !== "function") { _hubs = _hubs ?? []; _hubEmit(); return; }
    const r = await listHubs({}, studioRequestOptions("hubs"));
    const arr = (r as { hubs?: HubDto[] })?.hubs;
    _hubs = Array.isArray(arr) ? arr.map(hubFromDto) : [];
    _hubEmit();
  } catch { _hubs = _hubs ?? []; _hubEmit(); }
}
/** Re-run the hubs fetch (post-mutation). */
export function refetchHubs(): void { void _loadHubs(true); }
/** Remove a hub from the store (post-delete reflect). */
export function removeHubLocal(id: string): void { _hubs = (_hubs ?? []).filter((h) => h.id !== id); _hubEmit(); }
/** Upsert a hub DTO into the store (post duplicate/publish reflect). */
export function reflectHubDto(dto: HubDto): Hub {
  const h = hubFromDto(dto);
  const cur = _hubs ?? [];
  const i = cur.findIndex((x) => x.id === h.id);
  _hubs = i >= 0 ? cur.map((x) => (x.id === h.id ? h : x)) : [h, ...cur];
  _hubEmit();
  return h;
}

export function useHubs(): Hub[] {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((n) => n + 1);
    _hubSubs.add(cb);
    void _loadHubs();
    return () => { _hubSubs.delete(cb); };
  }, []);
  return _hubs ?? [];
}

type AnyFn = (...a: unknown[]) => Promise<unknown>;
const _op = (name: string): AnyFn | null => {
  const fn = (ApiClient as unknown as Record<string, unknown>)[name];
  return typeof fn === "function" ? (fn as AnyFn) : null;
};

function _hubDto(r: unknown): HubDto | null {
  const d = (r as { hub?: HubDto; data?: HubDto })?.hub ?? (r as { data?: HubDto })?.data ?? (r as HubDto);
  return d && (d as HubDto).id ? d as HubDto : null;
}
/** Duplicate a hub (`duplicateHub`, shipped). */
export async function duplicateHubOp(id: string): Promise<{ ok: true; hub: Hub } | { ok: false }> {
  const op = _op("duplicateHub");
  if (!op) return { ok: false };
  const dto = _hubDto(await op(id, studioRequestOptions("hubs")));
  return dto ? { ok: true, hub: reflectHubDto(dto) } : { ok: false };
}
/** Delete a hub (`deleteHub`). */
export async function deleteHubOp(id: string): Promise<{ ok: boolean }> {
  const op = _op("deleteHub");
  if (!op) return { ok: false };
  await op(id, studioRequestOptions("hubs"));
  removeHubLocal(id);
  return { ok: true };
}
/** Publish / unpublish a hub — no distinct verb by contract design;
 *  `updateHub({status})` drives it and sets `publishedAt`. */
export async function setHubPublish(id: string, status: "published" | "draft"): Promise<{ ok: true; hub: Hub } | { ok: false }> {
  const op = _op("updateHub");
  if (!op) return { ok: false };
  const dto = _hubDto(await op(id, { status }, studioRequestOptions("hubs")));
  return dto ? { ok: true, hub: reflectHubDto(dto) } : { ok: false };
}
/** Export hubs CSV (`exportHubs`). Returns a download URL when available. */
export async function exportHubsOp(): Promise<{ ok: true; downloadUrl?: string; filename?: string } | { ok: false }> {
  const op = _op("exportHubs");
  if (!op) return { ok: false };
  const r = await op({ format: "csv", scope: "filtered" }, studioRequestOptions("hubs")) as { downloadUrl?: string; filename?: string };
  return { ok: true, downloadUrl: r?.downloadUrl, filename: r?.filename };
}

/** Single-hub read for the editor (`getHub`, shipped). Falls back to finding the
 *  hub in the already-fetched list if the op is ever absent. */
export async function getHub(id: string, listFallback: Hub[]): Promise<Hub | null> {
  const op = _op("getHub");
  if (op) {
    try {
      const r = await op(id, studioRequestOptions("hubs")) as { hub?: HubDto } | HubDto;
      const dto = (r as { hub?: HubDto })?.hub ?? (r as HubDto);
      if (dto?.id) return hubFromDto(dto);
    } catch (e) { console.warn("getHub failed:", e); }
  }
  return listFallback.find((h) => h.id === id) ?? null;
}

/** Save a hub's draft (create if new, update if existing). Returns
 *  `{ ok:false }` only if the op is absent from the kit (drift guard) —
 *  a rejected API write THROWS to the caller, which surfaces the real error. */
export async function saveHub(hub: Hub, isNew: boolean): Promise<{ ok: true; hub: Hub } | { ok: false }> {
  const op = _op(isNew ? "createHub" : "updateHub");
  if (!op) return { ok: false };
  const r = await op(isNew ? hub : hub.id, hub, studioRequestOptions("hubs")) as { hub?: HubDto } | HubDto;
  const dto = (r as { hub?: HubDto })?.hub ?? (r as HubDto);
  const saved = dto?.id ? hubFromDto(dto) : hub;
  logUpsert("hub", isNew ? null : (hub as unknown as Record<string, unknown>), saved as unknown as Record<string, unknown>, { name: (saved as { name?: string }).name, action: isNew ? "created" : "updated" });
  return { ok: true, hub: saved };
}

/** Publish a hub (goes live). Contract has no `publishHub` verb — this is
 *  `updateHub({ status: 'published' })` (UIR-23: the old `_op("publishHub")`
 *  lookup resolved an op that never existed, so publish always "failed"). */
export async function publishHubOp(id: string): Promise<{ ok: true; hub: Hub } | { ok: false }> {
  const r = await setHubPublish(id, "published");
  if (r.ok) logAction("hub", r.hub.id, (r.hub as { name?: string }).name || r.hub.id, "published");
  return r;
}
